import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, Share2 } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../../utils/api';
import toast from 'react-hot-toast';

export default function ShareNoteModal({ note, onClose, onShared, theme }) {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Load current shares
  const loadShares = useCallback(async () => {
    try {
      const response = await apiGet(`/notes/${note.id}`);
      if (response.ok) {
        const data = await response.json();
        setShares(data.note?.shares || []);
      }
    } catch (err) {
      console.error('Failed to load shares:', err);
    } finally {
      setLoading(false);
    }
  }, [note.id]);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  // Load initial suggestions (recent contacts) on mount
  const loadInitialUsers = useCallback(async () => {
    setSearching(true);
    try {
      const response = await apiGet('/notes/shareable_users?page=1');
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setPage(data.page || 1);
        setHasMore(!!data.has_more);
      }
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    loadInitialUsers();
  }, [loadInitialUsers]);

  // Server-side search (debounced). Resets pagination.
  const searchUsers = useCallback(async (q) => {
    if (!q) {
      loadInitialUsers();
      return;
    }
    setSearching(true);
    try {
      const response = await apiGet(`/notes/shareable_users?q=${encodeURIComponent(q)}&page=1`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setPage(data.page || 1);
        setHasMore(!!data.has_more);
      }
    } catch (err) {
      console.error('Failed to search users:', err);
    } finally {
      setSearching(false);
    }
  }, [loadInitialUsers]);

  // Load the next page and append to the list.
  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const qs = new URLSearchParams({ page: String(nextPage) });
      if (searchQuery.trim()) qs.set('q', searchQuery.trim());
      const response = await apiGet(`/notes/shareable_users?${qs.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setUsers(prev => [...prev, ...(data.users || [])]);
        setPage(data.page || nextPage);
        setHasMore(!!data.has_more);
      }
    } catch (err) {
      console.error('Failed to load more users:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [page, searchQuery]);

  useEffect(() => {
    if (!searchQuery) return;
    const timer = setTimeout(() => searchUsers(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchUsers]);

  const handleShare = async (userId, permission = 'read') => {
    try {
      const response = await apiPost(`/notes/${note.id}/share`, { user_id: userId, permission });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.errors?.[0] || errData.error || 'Failed to share');
      }
      const data = await response.json();
      toast.success(data.message);
      await loadShares();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleShareByEmail = async (email, permission = 'read') => {
    try {
      const response = await apiPost(`/notes/${note.id}/share`, { email, permission });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.errors?.[0] || errData.error || 'Failed to share');
      }
      const data = await response.json();
      toast.success(data.message);
      setSearchQuery('');
      await loadShares();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUnshare = async (userId) => {
    try {
      const response = await apiDelete(`/notes/${note.id}/unshare/${userId}`);
      if (!response.ok) throw new Error('Failed to remove share');
      toast.success('Share removed');
      await loadShares();
    } catch (err) {
      toast.error('Failed to remove share');
    }
  };

  const sharedUserIds = shares.map(s => s.shared_with.id);
  const isDark = theme === 'dark';

  const trimmedQuery = searchQuery.trim();
  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedQuery);
  const noVisibleMatch = users.filter(u => !sharedUserIds.includes(u.id)).length === 0;
  const showEmailInvite = !searching && looksLikeEmail && noVisibleMatch;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className={`w-full max-w-md rounded-xl shadow-xl ${isDark ? 'bg-gray-800' : 'bg-white'} max-h-[80vh] flex flex-col`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <Share2 size={18} />
            <h2 className="font-dm-serif font-semibold">Share Note</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700">
            <X size={18} />
          </button>
        </div>

        {/* Current Shares */}
        {shares.length > 0 && (
          <div className={`px-5 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <p className="text-xs font-rasa font-medium text-gray-500 mb-2">Shared with</p>
            <div className="space-y-2">
              {shares.map(share => (
                <div key={share.id} className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-[10px] font-medium">
                      {(share.shared_with.first_name || share.shared_with.username || '?').charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-rasa font-medium">
                      {share.shared_with.first_name
                        ? `${share.shared_with.first_name} ${share.shared_with.last_name || ''}`.trim()
                        : share.shared_with.username}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {/* Permission toggle */}
                    <select
                      value={share.permission}
                      onChange={(e) => handleShare(share.shared_with.id, e.target.value)}
                      className={`text-[11px] font-rasa px-1.5 py-0.5 rounded border ${
                        isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      }`}
                    >
                      <option value="read">View</option>
                      <option value="edit">Edit</option>
                    </select>
                    <button
                      onClick={() => handleUnshare(share.shared_with.id)}
                      className="text-[11px] font-rasa text-red-500 hover:text-red-600 px-1"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="px-5 py-3">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or username..."
              className="bg-transparent outline-none text-sm font-rasa flex-1"
              autoFocus
            />
          </div>
          <p className={`text-[10px] font-rasa mt-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Search by name, username, or email
          </p>
        </div>

        {/* User List */}
        <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-1">
          {searching && (
            <p className={`text-xs font-rasa text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Searching...</p>
          )}

          {!searching && users.length === 0 && !showEmailInvite && (
            <p className={`text-xs font-rasa text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              No matching users found{trimmedQuery ? '. Type an email to invite directly.' : ''}
            </p>
          )}

          {showEmailInvite && (
            <div className={`flex items-center justify-between p-2 rounded-lg border ${
              isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-[10px] font-medium flex-shrink-0">
                  @
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-rasa font-medium truncate">{trimmedQuery}</div>
                  <div className={`text-[10px] font-rasa ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Share directly by email
                  </div>
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button
                  onClick={() => handleShareByEmail(trimmedQuery, 'read')}
                  className="px-2 py-1 text-xs font-rasa bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  View
                </button>
                <button
                  onClick={() => handleShareByEmail(trimmedQuery, 'edit')}
                  className="px-2 py-1 text-xs font-rasa bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Edit
                </button>
              </div>
            </div>
          )}

          {users
            .filter(u => !sharedUserIds.includes(u.id))
            .map(user => (
              <div
                key={user.id}
                className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
              >
                <div className="flex items-center gap-2">
                  {user.avatar_url ? (
                    <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-[10px] font-medium">
                      {(user.first_name || user.username || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <span className="text-sm font-rasa font-medium">
                      {user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user.username}
                    </span>
                    <span className={`ml-2 text-[10px] font-rasa ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      @{user.username}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleShare(user.id, 'read')}
                    className="px-2 py-1 text-xs font-rasa bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    View
                  </button>
                  <button
                    onClick={() => handleShare(user.id, 'edit')}
                    className="px-2 py-1 text-xs font-rasa bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}

          {hasMore && !searching && (
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className={`w-full mt-2 py-2 text-xs font-rasa rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
